import React, {
  ReactElement,
  useEffect,
  useState,
  useCallback,
  useReducer,
} from 'react';
import { Entity } from '../../types/entities/Entity';
import {
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableContainer,
  Typography,
} from '@mui/material';
import api from '../../services/api';
import { debounce } from 'lodash';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { formatNumber } from '../../reducer/actions/formatNumber';
import { GroupFinanceRow } from './GroupFinanceRow';

type Props<T extends Entity> = {
  values: any;
  allValues: any;
  schema: any;
  onChange: (value: any, column: string) => void;
};

const reducer = (state: any, action: any) => {
  switch (action.type) {
    case 'SET_INPUT':
      const newCurrent: any = { ...state.current };
      const newHistory: any = { ...state.history };

      if (action.payload.type === 'group_size') {
        newHistory.group_size = action.payload.value;
        newCurrent.group_size = action.payload.value;
      } else {
        const finances = [...newCurrent.finances];
        const newFinances = finances.map((finance: any) => {
          if (finance?.id === action.payload.id) {
            finance[`${action.payload.column}`] = action.payload.value;
          }
          return finance;
        });

        newHistory.finances = newFinances;
        newCurrent.finances = newFinances;
      }

      return {
        ...state,
        current: newCurrent,
        history: newHistory,
        send: true,
        lastChangedColumn: action.payload.type,
      };
    case 'SET_HISTORY':
      const tempAction: any = { ...action };

      return {
        ...state,
        history: {},
        current: tempAction.payload,
        send: false,
      };
    default:
      return state;
  }
};

export const ActivityReservationFinance = <T extends Entity>({
  values,
  allValues,
  schema,
  onChange,
}: Props<T>): ReactElement => {
  const [loading, setLoading] = useState<boolean>(false);
  const [eurCurrentValue, setEurCurrentValue] = useState<any>(undefined);

  const [state, dispatch] = useReducer(reducer, {
    history: [],
    current: {
      group_size: values?.activity_reservation?.group_size,
      finances: [],
    },
    send: false,
  });

  const fetchData = useCallback(() => {
    setLoading(true);
    const searchParams = new URLSearchParams({});
    api
      .get(`/activity-reservation/${values.id}/finances`, searchParams)
      .then((res: any) => {
        dispatch({
          type: 'SET_HISTORY',
          payload: {
            sales_partner: res?.data?.sales_partner,
            currency: res?.data?.currency,
            finances: res?.data?.finances,
            sum: res?.data?.sum,
          },
        });
        setLoading(false);
      });
  }, [values]);
  const fetchExchangeRate = useCallback(async () => {
    const response = await fetch(
      `https://api.exchangerate-api.com/v4/latest/EUR`
    );
    const data = await response.json();
    setEurCurrentValue(data.rates.HUF);
  }, []);
  const handleRefreshFinance = () => {
    setLoading(true);
    api
      .post(`/service-reservations/update-finance/${values.id}`, values)
      .then((res: any) => {
        fetchData();
        setLoading(false);
      });
  };
  const handleSaveData = (column: any, value: any, type: any, id?: any) => {
    dispatch({
      type: 'SET_INPUT',
      payload: {
        type: type,
        column: column,
        value: value,
        id: id,
      },
    });
  };

  const debouncedChange = useCallback(
    debounce(() => {
      if (state.send) {
        if (state.lastChangedColumn === 'group_size') {
          onChange(state.lastChangedColumn, state.history['group_size']);
        } else {
          onChange(state.lastChangedColumn, state.history['finances']);
        }
      }
    }, 500),
    [state]
  );

  const [sum, setSum] = useState<any>({
    sum: 0,
    sum_eur: 0,
    pp_sum: 0,
    pp_sum_eur: 0,
  });

  useEffect(() => {
    debouncedChange();
  }, [state, debouncedChange]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    //fetchExchangeRate();
  }, []);

  useEffect(() => {
    if (eurCurrentValue) {
      let sum = 0;
      let sumEur = 0;

      state.current.finances.map((finance: any) => {
        if (finance?.currency_id === 2) {
          sum += finance.gross_cost;
        }
        if (finance?.currency_id === 1) {
          sumEur += finance.gross_cost;
        }
      });

      sumEur = parseFloat((sumEur * eurCurrentValue).toFixed(2));

      setSum({
        sum: sum + sumEur,
        sum_eur: parseFloat(((sum + sumEur) / eurCurrentValue).toFixed(2)),

        pp_sum: parseInt(
          ((sum + sumEur) / state.current.group_size).toFixed(0)
        ),
        pp_sum_eur: parseInt(
          ((sum + sumEur) / state.current.group_size / eurCurrentValue).toFixed(
            0
          )
        ),
      });
    }
  }, [state, eurCurrentValue]);

  return (
    <>
      {loading ? (
        <>
          <Skeleton
            sx={{
              mt: '10px',
            }}
            variant="rounded"
            width={'100%'}
            height={50}
          />
          <Skeleton
            sx={{
              mt: '10px',
            }}
            variant="rounded"
            width={'100%'}
            height={50}
          />
          <Skeleton
            sx={{
              mt: '10px',
            }}
            variant="rounded"
            width={'100%'}
            height={200}
          />
        </>
      ) : (
        <>
          <Stack
            direction={'column'}
            alignItems={'flex-start'}
            sx={{
              marginBottom: '20px',
              marginTop: '20px',
            }}
          >
            <Stack direction={'row'} alignItems={'center'}>
              <Typography
                variant={'caption'}
                fontWeight={'bold'}
                marginRight={'10px'}
              >
                Sales Partner:
              </Typography>
              <Typography variant={'caption'} marginRight={'10px'}>
                {state.current?.sales_partner?.name
                  ? state.current?.sales_partner?.name
                  : '-'}
              </Typography>
            </Stack>
            <Stack direction={'row'} alignItems={'center'}>
              <Typography
                variant={'caption'}
                fontWeight={'bold'}
                marginRight={'10px'}
              >
                Currency:
              </Typography>
              <Typography variant={'caption'} marginRight={'10px'}>
                {state.current?.currency?.name}
              </Typography>
            </Stack>

            {/*    <IconButton onClick={() => handleRefreshFinance()}>
              <RefreshIcon color={'primary'} />
            </IconButton>*/}
          </Stack>

          <TableContainer component={Paper}>
            <Table
              aria-label="simple table"
              size="small"
              sx={{
                '& .MuiTableBody-root .MuiTableRow-root': {
                  background: '#fff',
                },
                '& .MuiTableCell-root': {
                  padding: '10px',
                },
                '& .MuiInputBase-root.MuiOutlinedInput-root': {
                  paddingRight: '0px',
                },
              }}
            >
              <TableHead
                sx={{
                  '& .MuiTableCell-root': {
                    color: 'inherit',
                    border: 'none',
                  },
                  '& .MuiTableRow-root .MuiTableCell-root.MuiTableCell-head': {
                    border: 'none',
                    fontSize: '12px',
                    p: '10px',
                  },
                  background: '#256861',
                  color: '#fff',
                }}
              >
                <TableRow
                  sx={{
                    '& .MuiTableCell-root': {
                      overflowWrap: 'anywhere',
                      padding: '2px',
                    },
                  }}
                >
                  <TableCell>Item</TableCell>
                  <TableCell align={'right'}>Size</TableCell>
                  <TableCell align={'right'}>Price PP</TableCell>
                  <TableCell align={'right'}>∑ Price</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {state.current.finances.map((finance: any) => {
                  return (
                    <GroupFinanceRow
                      values={finance}
                      onChange={(value, column) => {
                        //handleSaveData(column, value, 'finances', finance.id);
                      }}
                    />
                  );
                })}

                {!!(state.current?.sum?.EUR && state.current?.sum?.EUR > 0) && (
                  <TableRow
                    sx={{
                      '& .MuiFormControl-root-MuiTextField-root input': {
                        textAlign: 'right',
                      },
                    }}
                  >
                    <TableCell colSpan={4}>
                      {state.current?.sum?.EUR} EUR
                    </TableCell>
                  </TableRow>
                )}

                {!!(state.current?.sum?.HUF && state.current?.sum?.HUF > 0) && (
                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{
                        textAlign: 'left',
                        fontWeight: 'bold',
                      }}
                    >
                      Total:
                    </TableCell>
                    <TableCell
                      colSpan={2}
                      sx={{
                        textAlign: 'right',
                      }}
                    >
                      {formatNumber(state.current?.sum?.HUF)} HUF
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/*   <Stack direction={'row'} mt={'30px'}>
            <Stack width={'50%'}></Stack>
            <Stack width={'50%'} direction={'column'}>
              <Stack direction={'row'} alignItems={'center'}>
                <Box
                  sx={{
                    width: '30px',
                  }}
                >
                  <b>∑</b>
                </Box>
                <Stack direction={'column'} width={'100%'}>
                  <Typography variant={'subtitle1'} fontWeight={'bold'}>
                    {`${formatNumber(sum.sum)} HUF`}
                  </Typography>
                  <Typography variant={'subtitle1'} fontWeight={'bold'}>
                    {`${formatNumber(sum.sum_eur)} EUR`}
                  </Typography>
                  <Divider />
                </Stack>
              </Stack>

              <Stack direction={'row'} alignItems={'center'}>
                <Box
                  sx={{
                    width: '30px',
                  }}
                >
                  <b>PP</b>
                </Box>
                <Stack direction={'column'} width={'100%'}>
                  <Typography variant={'subtitle1'} fontWeight={'bold'}>
                    {`${formatNumber(sum.pp_sum)} HUF`}
                  </Typography>
                  <Typography variant={'subtitle1'} fontWeight={'bold'}>
                    {`${formatNumber(sum.pp_sum_eur)} EUR`}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </Stack>*/}
        </>
      )}
    </>
  );
};
